'use client';

import React from 'react';
import PropTypes from 'prop-types';

import { Box, Card, Chip, Stack, Checkbox, CardMedia, CardHeader, Typography, CardContent } from '@mui/material';

import { getPostType } from 'src/utils/platform/platform-info';
import { extractUniqueHashtags } from 'src/utils/text/hashtag-extract';
import { highlightTextWithHtmlRemoval } from 'src/utils/text/text-highlight';
import { formatNumber, formatTimestamp } from 'src/utils/format/format-utils';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { readNegativeEvidence } from 'src/utils/moments/view/negative-evidence';

import { RADIUS } from 'src/theme/ui-tokens';
import { languageMap } from 'src/constants/languages';

import SafeHtml from 'src/components/cmui/post-card/SafeHtml';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';
import { PostCardCampaignOverlay } from 'src/components/cmui/campaign';
import PostMediaBadge from 'src/components/cmui/post-card/PostMediaBadge';
import HighlightedText from 'src/components/cmui/post-card/HighlightedText';

import PostKindChips, { getPostKindFlags } from './PostKindChips';


const MAX_VISIBLE_BRAND_CHIPS = 3;

/**
 * 재사용 가능한 게시물 카드 컴포넌트
 * 게시물의 상태(이미지, 제목, 내용 조합)에 따라 유연하게 UI 조정
 *
 * ⚠️ `post.*` 를 `@param` 으로 하나씩 적지 않는다 — 그러면 `.ts`/`.tsx` 호출부가
 *    「id·title·content… 를 최상위 prop 으로 넘겨라」로 읽는다. 받는 것은 아래
 *    `PostCardPost` 한 덩어리다.
 *
 * @typedef {Object} PostCardPost
 * @property {string} [id] - 게시물 ID
 * @property {string} [title] - 제목
 * @property {string} [content] - 내용
 * @property {string} [author] - 작성자
 * @property {string} [account] - 계정명 (@username)
 * @property {string} [source] - 플랫폼 소스 (instagram, twitter 등)
 * @property {string} [imageUrl] - 이미지 URL
 * @property {string} [imageS3Url] - S3 이미지 URL
 * @property {number|string} [timestamp] - 작성 시간
 * @property {Array} [brands] - 브랜드 태그 배열
 * @property {number} [likeCount] - 좋아요 수
 * @property {number} [commentCount] - 댓글 수
 * @property {number} [viewCount] - 조회수
 *
 * @typedef {Object} PostCardProps
 * @property {PostCardPost & Record<string, any>} post - 게시물 데이터
 * @property {Function} [onClick] - 카드 클릭 핸들러 (상세 모달 열기용)
 * @property {string} [searchKeyword] - 서버 검색어 (하이라이트용)
 * @property {string} [clientSearch] - 화면에서 다시 좁힌 검색어
 * @property {Array} [topicKeywords] - 주제 키워드 (하이라이트용)
 * @property {*} [expandedSelectedBrands] - 펼친 브랜드 선택 상태
 * @property {boolean} [external] - 외부 임베드 여부
 * @property {boolean} [isQuote] - 인용글 여부
 * @property {boolean} [showKindChips] - 유형 칩 노출
 * @property {boolean} [hidePlatformIcon] - 플랫폼 아이콘 숨김
 * @property {boolean} [showBookmarkButton] - 북마크 버튼 노출
 * @property {boolean} [enableCampaignAdd] - 캠페인 담기 허용
 * @property {boolean} [enableImageBookmark] - 이미지 북마크 허용
 * @property {*} [bookmarkTargetPost] - 유형 판정에 쓸 최종글
 * @property {boolean} [isBookmark] - 북마크된 상태
 * @property {Set} [selectedPostIds] - 선택된 게시물 id 집합
 * @property {Function} [onToggleSelect] - 선택 토글
 *
 * @param {PostCardProps} props
 */
function PostCard({
  post,
  onClick,
  searchKeyword = '',
  clientSearch = '',
  topicKeywords = [],
  expandedSelectedBrands,
  external = false,
  isQuote = false,
  showKindChips = true,
  hidePlatformIcon,
  showBookmarkButton = true,
  enableCampaignAdd = false,
  enableImageBookmark = false,
  bookmarkTargetPost,
  isBookmark = false,
  selectedPostIds = new Set(),
  onToggleSelect,
  actionSlot,
}: {
  post: any
  onClick?: (post: any) => void
  searchKeyword?: string | readonly string[]
  clientSearch?: string
  topicKeywords?: readonly string[]
  /** ⚠️ `Set` 도 배열도 온다 — 코드가 `.has` 유무로 오리 타이핑한다 */
  expandedSelectedBrands?: any
  external?: boolean
  isQuote?: boolean
  showKindChips?: boolean
  hidePlatformIcon?: boolean
  showBookmarkButton?: boolean
  enableCampaignAdd?: boolean
  enableImageBookmark?: boolean
  bookmarkTargetPost?: any
  /**
   * 카드 **발치의 동작 묶음에 끼워 넣을 것** — 북마크 옆에 나란히 선다.
   * 🔑 부르는 화면이 제 단추를 «카드 밖»에 띄우지 않게 하려고 연 자리다(담기·비교 같은 것).
   * ⚠️ 여기 오는 것은 **아이콘 한 칸**이다 — 글자 단추를 넣으면 발치 줄이 무너진다.
   */
  actionSlot?: React.ReactNode
  isBookmark?: boolean
  selectedPostIds?: ReadonlySet<string>
  onToggleSelect?: (...args: any[]) => void
}) {
  // 이 카드가 "원본 글"을 보여주더라도(예: PostCardRepost 내부),
  // 댓글/인용/리포스트 같은 "유형"은 최종글(bookmarkTargetPost) 기준으로 표시해야 함
  const kindPost = bookmarkTargetPost ?? post

  const {
    // id,
    title,
    content,
    author,
    account,
    source,
    imageUrl,
    imageS3Url,
    timestamp,
    date,
    market,
    canonicalBrands,
    brands = []
  } = post;


  // canonicalBrand 필드가 존재하면 사용 (빈 배열이어도 사용), 없으면 기존 brands 필드 사용
  const displayBrands = canonicalBrands !== undefined
    ? (Array.isArray(canonicalBrands) ? canonicalBrands : [])
    : (Array.isArray(brands) ? brands : []);

  // sentiment 데이터 직접 추출 (구조 분해 할당 대신)
  const sentimentEvidencePositive = post?.sentimentEvidencePositive || [];
  const sentimentEvidenceNegative = readNegativeEvidence(post);
  const isHit = Boolean(post?.isHit);
  // isQuote는 prop으로 받거나 post에서 가져옴
  const isQuoteValue = isQuote || Boolean(kindPost?.isQuote);
  const kindChipFlags = {
    ...getPostKindFlags(kindPost),
    isQuote: isQuoteValue,
  };

  // 플랫폼 정보 utils로 추출
  // 아이콘 경로만 사용
  const platformIconPath = source ? `/assets/icons/platforms/${String(source).toLowerCase()}.svg` : null;

  // external prop이 true인 경우 (PostCardRepost 안에 있는 PostCard) 플랫폼 아이콘 숨김
  // 단, 원문 카드에서도 채널 아이콘을 보여야 하는 케이스가 있어 override 가능
  const shouldHidePlatformIcon = hidePlatformIcon ?? external;

  const formatTimestampFn = formatTimestamp;

  const latestMetrics = getMetricsByScoreTime(post);

  // 해시태그 추출
  const allHashtags = extractUniqueHashtags(title, content);

  // 이미지 우선순위: imageS3Url > imageUrl > null
  const imageSrc = imageS3Url || imageUrl || null;

  // 게시물 상태에 따른 UI 조정
  const hasImage = !!imageSrc;
  const hasTitle = !!title;
  const hasContent = !!content;

  // 게시물 타입 결정
  const postType = getPostType(post);


  // 텍스트 하이라이트: util에 clientSearch를 직접 전달 (중앙화)
  const highlightText = (text: unknown, searchKw?: unknown, topicKws?: unknown) =>
    highlightTextWithHtmlRemoval(
      text as string,
      searchKw as string,
      topicKws as string[],
      clientSearch,
      sentimentEvidencePositive,
      sentimentEvidenceNegative
    );

  /**
   * 🔑 신원(계정명·작성자명)은 **마크업 없이** 그린다 — `dangerouslySetInnerHTML` 을 쓰지 않는다.
   * 그 값에 태그가 필요한 이유가 없는데 HTML 통로를 열어 두면 «글쓴이가 고른 문자열»이
   * 태그가 된다. React 가 텍스트를 늘 escape 하므로 여기서는 태그가 생길 수 없다.
   */
  const renderIdentity = (text: string | null | undefined, prefix = '') => (
    <HighlightedText
      text={text}
      prefix={prefix}
      searchKeyword={searchKeyword as string}
      topicKeywords={topicKeywords as string[]}
      clientSearch={clientSearch}
      sentimentPositive={sentimentEvidencePositive}
      sentimentNegative={sentimentEvidenceNegative}
    />
  );

  // market 코드 → 표시 이름 매핑 (없으면 원본 표시)
  const marketDisplay = React.useMemo(() => {
    if (!market) return null;
    const code = String(market).toLowerCase();
    const found = Array.isArray(languageMap)
      ? languageMap.find((l) => String(l.code).toLowerCase() === code)
      : null;
    return found?.name || market;
  }, [market]);

  // 시간 표시: timestamp 우선, 없으면 date 사용
  const timeDisplay = React.useMemo(() => {
    if (timestamp) return formatTimestampFn(timestamp);
    if (date) return formatTimestampFn(date);
    return '';
  }, [timestamp, date, formatTimestampFn]);

  return (
    <Card
      sx={{
        borderRadius: external ? 1 : 2,
        bgcolor: 'white',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        overflow: 'visible', // 리포스트 아이콘이 카드 밖으로 나올 수 있도록
        p: external ? 1 : 2,
        width: '100%',
        maxWidth: 1440,
        mx: 'auto',
        height: 'fit-content',  // 추가: 내용에 맞춰 높이 조정
        display: 'flex',        // 추가: flex 컨테이너로 변경
        flexDirection: 'column', // 추가: 세로 방향
        position: 'relative', // 리포스트 아이콘 위치 지정을 위해
        cursor: isQuoteValue ? 'default' : 'pointer',
        '&:hover, &:active, &.Mui-focusVisible': isQuoteValue ? {} : {
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          transform: 'none',
        },
      }}
      onClick={isQuoteValue ? undefined : () => onClick && onClick(post)}
    >
      {/* 헤더 영역 */}
      <CardHeader
        title={
          <Stack direction="column" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
            {showKindChips && <PostKindChips post={post} flags={kindChipFlags} />}

            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', maxWidth: '100%', flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0 }}>
              {/* 북마크 모드: 날짜 행 왼쪽 끝에 체크박스 (리포스트/인용 시 bookmarkTargetPost.id 사용) */}
              {isBookmark && onToggleSelect && (
                <Stack sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    size="small"
                    checked={selectedPostIds?.has?.(bookmarkTargetPost?.id ?? post?.id) ?? false}
                    onChange={() => onToggleSelect(bookmarkTargetPost?.id ?? post?.id)}
                    sx={{ p: 0 }}
                    aria-label={`${post?.title ?? '게시물'} 선택`}
                  />
                </Stack>
              )}
              {/* 왼쪽: 아이콘 + 작성자 (한 줄, 작성자 ellipsis) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, overflow: 'hidden' }}>
                {platformIconPath && !shouldHidePlatformIcon && (
                  <Box
                    component="img"
                    src={platformIconPath}
                    alt={source}
                    sx={{ width: 18, height: 18, flexShrink: 0 }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, overflow: 'hidden', flex: 1 }}>
                  <Typography
                    fontWeight={600}
                    fontSize="15px"
                    sx={{
                      color: 'black',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                      lineHeight: 1.2,
                    }}
                    title={author}
                  >
                    {renderIdentity(`${author || ''}`)}
                  </Typography>
                  {account && (
                    <Typography
                      component="span"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '13.5px',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                        flexShrink: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: { xs: '50%', sm: '40%' },
                      }}
                      title={`@${account}`}
                    >
                      {renderIdentity(`${account || ''}`, '@')}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* 오른쪽: Hit 배지 / 국가 표시 */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {isHit && (
                  <Box sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: RADIUS.sm,
                    bgcolor: 'error.lighter',
                    color: 'error.dark',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}>
                    HIT
                  </Box>
                )}
                {marketDisplay && (
                  <Typography color="grey.600" variant="caption" sx={{ fontSize: '15px', fontWeight: 'light' }}>
                    {marketDisplay}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        }
        sx={{
          pb: 1.5, // 하단 패딩 제거
          px: 0, // 좌우 패딩 제거 (카드의 30px 패딩 사용)
          pt: 0, // 상단 패딩 제거
          '& .MuiCardHeader-content': { maxWidth: '100%', overflow: 'hidden', minWidth: 0 },
        }}
      />

      {/* 컨텐츠 영역 */}
      <CardContent sx={{
        pt: 0,
        pb: 0, // 하단 패딩 제거
        px: 0, // 좌우 패딩 제거 (카드의 30px 패딩 사용)
        '&.MuiCardContent-root': {
          paddingBottom: 0, // MUI 기본 bottom 패딩 제거
        },
      }}>
        {/* 이미지 - 이미지가 있는 경우에만 표시 */}
        {hasImage && (
          <Box
            mb={hasTitle || hasContent ? 2 : 0}
            sx={{
              position: 'relative',
              borderRadius: RADIUS.lg,
              overflow: 'hidden',
            }}
          >
            <PostMediaBadge post={post} iconOnly />
            <CardMedia
              component="img"
              image={imageSrc}
              alt="post media"
              onError={(e) => ((e.target as HTMLElement).style.display = "none")}
              sx={{
                borderRadius: RADIUS.lg,
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                width: '100%',
                maxHeight: 'none',
                display: 'block',
              }}
            />
          </Box>
        )}

        {/* 제목 - 제목이 있는 경우에만 표시 */}
        {hasTitle && (
          <Typography
            // variant={postType === 'title-only' ? 'h6' : 'subtitle1'}
            sx={{
              color: 'black',
              // mb: hasContent ? 0 : { xs: 1, sm: 2 }, // 16px 간격
              display: '-webkit-box',
              WebkitLineClamp: postType === 'title-only' ? 5 : 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: { xs: '13.5px', sm: '15px' },
              // fontWeight: 'Bold',
              textAlign: 'left',
            }}
          >
            <SafeHtml html={highlightText(title, searchKeyword, topicKeywords)} />
          </Typography>
        )}

        {/* 내용 - 내용이 있는 경우에만 표시 */}
        {hasContent && !external && (
          <Typography
            variant="body2"
            sx={{
              // mb: { xs: 1, sm: 2 }, // 16px 간격
              color: 'text.secondary',
              display: '-webkit-box',
              WebkitLineClamp: postType === 'text-only' ? 4 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.5,
              textAlign: 'left',
              // fontWeight: 'Medium'
              // fontWeight: 'Regular'
            }}
          >
            <SafeHtml html={highlightText(content, searchKeyword, topicKeywords)} />
          </Typography>
        )}

        {/* 브랜드와 해시태그 태그 */}
        {((displayBrands.length > 0 || allHashtags.length > 0) && !external) && (
          <Stack spacing={0.5} my={{ xs: 1, sm: 2 }}>
            {/* 브랜드 태그 */}
            {displayBrands.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                {displayBrands.slice(0, MAX_VISIBLE_BRAND_CHIPS).map((brand, i) => {
                  const isSelected = expandedSelectedBrands && expandedSelectedBrands.has &&
                    Array.from<any>(expandedSelectedBrands).some(selectedBrand =>
                      selectedBrand.toLowerCase() === brand.toLowerCase()
                    );
                  return (
                    <Chip
                      key={`brand-${brand}-${i}`}
                      label={brand}
                      size="small"
                      sx={isSelected ? {
                        mr: 0.25,
                        mb: 0.25,
                        minHeight: 24,
                        height: 24,
                        fontSize: '11px',
                        '& .MuiChip-label': { px: 1, py: 0, lineHeight: 1 },
                        fontWeight: 600,
                        bgcolor: '#fbbf24',
                        color: '#78350f',
                        border: '1.5px solid #f59e1a',
                        pointerEvents: 'none',
                        cursor: 'default',
                        boxShadow: 'none',
                        outline: 'none',
                        borderRadius: RADIUS.pill,
                        '&:hover, &:focus, &:active': {
                          bgcolor: '#fbbf24',
                          color: '#78350f',
                          border: '1.5px solid #f59e1a',
                          boxShadow: 'none',
                          outline: 'none',
                        },
                      } : {
                        mr: 0.25,
                        mb: 0.25,
                        minHeight: { xs: 24, sm: 26 },
                        height: { xs: 24, sm: 26 },
                        fontSize: '11px',
                        '& .MuiChip-label': { px: 1, py: 0, lineHeight: 1 },
                        fontWeight: 600,
                        bgcolor: 'primary.lighter',
                        color: 'primary.dark',
                        pointerEvents: 'none',
                        cursor: 'default',
                        borderRadius: RADIUS.pill,
                        '&:hover, &:focus, &:active': {
                          bgcolor: 'primary.lighter',
                          color: 'primary.dark',
                          boxShadow: 'none',
                          outline: 'none',
                        },
                      }}
                    />
                  );
                })}
                {displayBrands.length > MAX_VISIBLE_BRAND_CHIPS && (
                  <Typography color="primary.dark" variant="caption">
                    {`+${displayBrands.length - MAX_VISIBLE_BRAND_CHIPS}`}
                  </Typography>
                )}
              </Stack>
            )}


          </Stack>
        )}

        {/* 리포스트/인용 카드 내부 원문(external)은 메타(수치칩/날짜/북마크) 미표시 */}
        {!external && (
          <Stack
            direction="row"
            spacing={1} // 8px 간격
            mt={{ xs: 1, sm: 2 }} // 16px 간격
            mb={0} // 하단 마진 제거
            alignItems="center"
            justifyContent="flex-start" // 왼쪽 정렬
            flexWrap="wrap"
          >
            {/* 좋아요 */}
            {!isQuoteValue && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5, // 아이콘과 숫자 간격
                  height: 26,
                  bgcolor: 'grey.100',
                  borderRadius: RADIUS.pill,
                  px: 1.5, // 10px 패딩
                  py: 0.625, // 5px 세로 패딩
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/icon_svg/like.svg"
                  sx={{
                    width: 16,
                    height: 16,
                    filter: 'brightness(0) saturate(100%)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'black',
                    fontWeight: 'Medium',
                    fontSize: '13.5px',
                  }}
                >
                  {formatNumber(latestMetrics.likeCount)}
                </Typography>
              </Box>
            )}

            {/* 댓글 */}
            {!isQuoteValue && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5, // 아이콘과 숫자 간격
                  height: 26,
                  bgcolor: 'grey.100',
                  borderRadius: RADIUS.pill,
                  px: 1.5, // 10px 패딩
                  py: 0.625, // 5px 세로 패딩
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/icon_svg/reply.svg"
                  sx={{
                    width: 16,
                    height: 16,
                    filter: 'brightness(0) saturate(100%)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'black',
                    fontWeight: 'Medium',
                    fontSize: '13.5px',
                  }}
                >
                  {formatNumber(latestMetrics.commentCount)}
                </Typography>
              </Box>
            )}

            {/* 조회수 */}
            {!isQuoteValue && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5, // 아이콘과 숫자 간격
                  height: 26,
                  bgcolor: 'grey.100',
                  borderRadius: RADIUS.pill,
                  px: 1.5, // 10px 패딩
                  py: 0.625, // 5px 세로 패딩
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/icon_svg/view.svg"
                  sx={{
                    width: 16,
                    height: 16,
                    filter: 'brightness(0) saturate(100%)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'black',
                    fontWeight: 'Medium',
                    fontSize: '13.5px',
                  }}
                >
                  {post?.contentsType === 'instagram_post' && latestMetrics.viewCount === 0
                    ? '-'
                    : formatNumber(latestMetrics.viewCount)}
                </Typography>
              </Box>
            )}

            <Stack direction="row" width='100%' justifyContent='space-between' spacing={1} alignItems="center">

              {timeDisplay && (
                <Typography color="text.secondary" variant="caption" sx={{

                  textAlign: 'right',
                  fontWeight: 'Light',
                  fontSize: '13.5px',
                  color: 'text.secondary',
                }}>
                  {timeDisplay}
                </Typography>
              )}
              {!isBookmark && (showBookmarkButton || enableCampaignAdd || actionSlot) && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  onClick={(e) => e.stopPropagation()}
                >
                  {actionSlot}
                  {showBookmarkButton && (
                    <PostCardBookmarkOverlay
                      placement="postCard"
                      post={bookmarkTargetPost ?? post}
                    />
                  )}
                  {enableCampaignAdd && (
                    <PostCardCampaignOverlay
                      placement="postCard"
                      post={bookmarkTargetPost ?? post}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

PostCard.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    author: PropTypes.string,
    account: PropTypes.string,
    source: PropTypes.string,
    imageUrl: PropTypes.string,
    imageS3Url: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    market: PropTypes.string,
    indexedAt: PropTypes.string,
    brands: PropTypes.arrayOf(PropTypes.string),
    canonicalBrands: PropTypes.arrayOf(PropTypes.string),
    likeCount: PropTypes.number,
    commentCount: PropTypes.number,
    viewCount: PropTypes.number,
    ingredients: PropTypes.arrayOf(PropTypes.string),
    concerns: PropTypes.arrayOf(PropTypes.string),
    persona_age: PropTypes.string,
    persona_gender: PropTypes.string,
    sentimentEvidencePositive: PropTypes.arrayOf(PropTypes.string),
    sentimentEvidenceNegative: PropTypes.arrayOf(PropTypes.string),
    metrics: PropTypes.objectOf(PropTypes.shape({
      likeCount: PropTypes.number,
      commentCount: PropTypes.number,
      viewCount: PropTypes.number,
      requestDateTime: PropTypes.string,
    })),
  }).isRequired,
  external: PropTypes.bool,
  onClick: PropTypes.func,
  searchKeyword: PropTypes.string,
  topicKeywords: PropTypes.arrayOf(PropTypes.string),
  expandedSelectedBrands: PropTypes.objectOf(PropTypes.bool),
  enableImageBookmark: PropTypes.bool,
  enableCampaignAdd: PropTypes.bool,
  bookmarkTargetPost: PropTypes.object,
};

export default React.memo(PostCard); 